/* eslint-disable max-len */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { ModalNext, ModalFooter } from '@jutro/components';
import { publish } from '@jutro/events';
import { WMICButton } from 'wmic-components-platform-react';
import { WalletService } from 'gw-capability-policy';
import { CONSTANTS, WMICTopics } from 'wmic-portals-utils-js';
import userProfileMessages from '../WMICInsuranceCardModal.messages';
import messages from './WMICIOSStepsModal.messages';
import styles from './WMICIOSStepsModal.module.scss';

import { Button } from '@jutro/legacy/components';

const WMICIOSStepsModal = (props) => {
    const {
        isOpen, onReject, onResolve, policyNumber, termNumber, vehicles, authHeader, sessionUUID, originPage
    } = props;
    const translator = useTranslator();
    const [disableDownloadIdCard, updateDisableDownloadIdCard] = useState(false);

    const isDetailsOrigin = originPage === 'details';
    const pageString = isDetailsOrigin
        ? 'my account:insurance:auto policy:download ios auto insurance card'
        : 'my account:manage my account:download ios auto insurance card';

    useEffect(() => {
        // "Download iOS Auto Insurance Card" modal displayed after clicking the "iOS customers" CTA.
        publish(WMICTopics.ROUTE_CHANGED, {
            channel: 'my account',
            page: pageString,
            subchannel1: isDetailsOrigin ? 'insurance' : 'manage my account',
            ...(isDetailsOrigin && { subchannel2: 'auto policy' }),
            sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '',
            state: (window.wins_data_layer?.state || '').toString().toLowerCase()
        });
    }, []);

    const downloadIdCard = (vehicleNumber) => {
        // ID card CTA clicked on the Download iOS Auto Insurance Card modal.
        publish(WMICTopics.CTA_CLICK, {
            channel: 'my account',
            cta: `${pageString}-add to apple wallet`,
            page: pageString,
            subchannel1: isDetailsOrigin ? 'insurance' : 'manage my account',
            ...(isDetailsOrigin && { subchannel2: 'auto policy' }),
            sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '',
            state: (window.wins_data_layer?.state || '').toString().toLowerCase()
        });

        updateDisableDownloadIdCard(true);
        WalletService.getIdCard(policyNumber, termNumber, vehicleNumber, authHeader, sessionUUID)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`ID Card request failed with status ${response.status}`);
                }
                // API returns JSON with passData.data containing the binary buffer
                return response.json();
            })
            .then((jsonResponse) => {
                // Extract the Buffer data from the JSON response
                if (!jsonResponse?.passData?.data) {
                    throw new Error('Missing passData in response');
                }

                // Convert the Buffer array to a proper Buffer, then wrap in Blob
                const passBuffer = Buffer.from(jsonResponse.passData.data);
                const passBlob = new Blob([passBuffer], { type: 'application/vnd.apple.pkpass' });

                // Create and trigger download via anchor element
                const url = window.URL.createObjectURL(passBlob);
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.type = 'application/vnd.apple.pkpass';
                anchor.setAttribute('download', 'pass.pkpass');
                anchor.style.display = 'none';
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);

                // Clean up object URL to prevent memory leaks
                setTimeout(() => window.URL.revokeObjectURL(url), 100);
                updateDisableDownloadIdCard(false);
            })
            .catch((error) => {
                updateDisableDownloadIdCard(false);
                onResolve(CONSTANTS.ERROR);
                console.error('ID Card download failed:', error);
            });
    };

    const handleClose = () => {
        onReject();
    };
    
    return (
        <ModalNext       
            isOpen={isOpen}
            onRequestClose={handleClose}
            shouldCloseOnEsc
            overlayClassName={styles['ww-payment-modal-overlay']}
            contentLayout={{
                component: 'Grid',
                componentProps: {
                    rows: ['auto'],
                },
            }} 
            >
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                <h3>{translator(userProfileMessages.insuranceIDCard)}</h3>
                    <p></p>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles['digital-wallet']}>
                        <h3 className={styles['ww-editorial-subhead']}>{translator(messages.followTheseSteps)}</h3>
                        <ol className={styles['wmic-ol']}>
                            <li>{translator(messages.onYourDevice)}</li>
                            <li>{translator(messages.idCardShouldAutomatically)}</li>
                            <li>{translator(messages.youShouldSeeAPreview)}</li>
                            <li>{translator(messages.theCardIsNowAdded)}</li>
                        </ol>
                        <h3 className={styles['ww-editorial-subhead']}>{translator(messages.selectTheVehicle)}</h3>
                        <div className="ww-poi-buttons">
                            {vehicles.map(({ year, make, model, vehicleNumber }) => (
                                    <div>
                                        <Button
                                            className={styles['ios-vehicle-poi-button-width']}
                                            onClick={() => downloadIdCard(vehicleNumber)}
                                            disabled={disableDownloadIdCard}
                                            trackButtonIdentifier={translator(messages.downloadIOSIdCardTrackButtonIdentifier)}
                                        >
                                            {`${year} ${make} ${model} (${vehicleNumber})`}
                                        </Button>
                                        <br />
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
                <ModalFooter>
                    <WMICButton
                        onClick={() => { onReject(); }}
                        type="primary"
                        className="ww-btn-primary"
                    >
                        {translator(messages.close)}
                    </WMICButton>
                </ModalFooter>
            </div>
        </ModalNext>
    );
};

WMICIOSStepsModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onReject: PropTypes.func.isRequired,
    onResolve: PropTypes.func.isRequired,
    policyNumber: PropTypes.string.isRequired,
    termNumber: PropTypes.number.isRequired,
    vehicles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    sessionUUID: PropTypes.string,
    originPage: PropTypes.string
};

WMICIOSStepsModal.defaultProps = {
    sessionUUID: null,
    originPage: 'home'
};

export default WMICIOSStepsModal;